import { uniq } from "@-/com.npmjs.array-utils-ts";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  CARD_CONTENT_OVERLAY_TABLES,
  resolveUserCardContentKind,
  resolveUserCardSubject,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import {
  type DeviceSettings,
  useDeviceSetting,
} from "@-/dev.improve.haniverse.lib.device-settings";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import type { DueUserCard } from "#use-due-user-cards-query";

/** One hardcoded review-queue slot, as the Admin panel stores it. */
export type PinnedReviewQueueEntry = DeviceSettings["pinnedReviewQueue"][number];

export type PinnedReviewQueueCard = {
  /**
   * The card as the due scan describes due cards, with `due` brought forward to `pinnedAt`: a
   * pinned card is due from the moment it was pinned. `null` when this actor holds no in-study card
   * with the entry's id (removed, or pinned while another account was open).
   */
  card: DueUserCard | null;
  entry: PinnedReviewQueueEntry;
  /** A live review of the card landed at or after `pinnedAt`, so the entry is spent. */
  isReviewed: boolean;
};

// A rating writes `user_card_review` (spending the head) and `fsrs_card` (its schedule); Undo
// soft-deletes that review, which un-spends it.
const PINNED_REVIEW_QUEUE_TABLES = [
  "user_card",
  "fsrs_card",
  "user_card_review",
  ...CARD_CONTENT_OVERLAY_TABLES,
] as const;

export function useGetPinnedReviewQueueQueryKey() {
  const [entries] = useDeviceSetting("pinnedReviewQueue");
  const getUserCardsQueryKey = actorQueries(import.meta.location).userCard.findMany.useGetQueryKey({
    with: {
      fsrsCard: true,
      userCardReviews: true,
      userGraphemeMeaningCard: true,
      userGraphemePronunciationCard: true,
      userWordSentenceCard: true,
    },
  });
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(PINNED_REVIEW_QUEUE_TABLES),
  );
  return () =>
    withTableChangeVersion(["pinnedReviewQueue", { entries, getUserCardsQueryKey }] as const);
}

/**
 * The hardcoded review queue (the `pinnedReviewQueue` device setting) resolved against the actor
 * database: every entry in stored order, with its card and whether it is already spent.
 *
 * Spending is derived from committed reviews rather than written back to the setting, the same
 * way the deck resolves every next card from committed state: rating the head spends it, and
 * undoing that rating (a soft-deleted review row) makes it the head again. A handful of cards, so
 * their whole live review history is read and compared to `pinnedAt` here.
 */
export const [usePinnedReviewQueueQueryOptions, pinnedReviewQueueQueryFn] = createUseQueryOptions(
  useGetPinnedReviewQueueQueryKey,
  async ({
    client,
    queryKey: [_, { entries, getUserCardsQueryKey }],
  }): Promise<PinnedReviewQueueCard[]> => {
    if (entries.length === 0) {
      return [];
    }
    const rows = await client.fetchQuery(
      actorQueries(import.meta.location).userCard.findMany.getQueryOptions(getUserCardsQueryKey, {
        where: {
          fsrsCardId: { isNotNull: true },
          id: { in: uniq(entries.map((entry) => entry.userCardId)) },
        },
      }),
    );
    const rowById = new Map(rows.map((row) => [row.id, row]));
    return entries.map((entry): PinnedReviewQueueCard => {
      const row = rowById.get(entry.userCardId);
      const scheduledDueMs = row?.fsrsCard == null ? Number.NaN : Date.parse(row.fsrsCard.due);
      const pinnedAtMs = Date.parse(entry.pinnedAt);
      if (row === undefined || Number.isNaN(scheduledDueMs)) {
        return { card: null, entry, isReviewed: false };
      }
      const dueMs = Math.min(scheduledDueMs, pinnedAtMs);
      return {
        card: {
          cardContentKind: resolveUserCardContentKind(row),
          due: new Date(dueMs).toISOString(),
          dueMs,
          subject: resolveUserCardSubject(row),
          userCardId: row.id,
        },
        entry,
        isReviewed: row.userCardReviews.some(
          (review) => Date.parse(review.reviewedAt) >= pinnedAtMs,
        ),
      };
    });
  },
);

/** The unspent entries' cards, in queue order: the head of the review queue. */
export function pendingPinnedReviewCards(queue: readonly PinnedReviewQueueCard[]): DueUserCard[] {
  return queue.flatMap(({ card, isReviewed }) => (card === null || isReviewed ? [] : [card]));
}
